import React, { useState, useRef, useEffect } from "react";
import { EmployeeRole, Workflow } from "../../types"; // Ensure correct import path for Workflow type

interface EmployeeRolesDropdownProps {
    label: string;
    availableRoles: EmployeeRole[];
    selectedRoles: EmployeeRole[];
    onSelectionChange: (selected: EmployeeRole[]) => void;
    isLoading: boolean;
}

const EmployeeRolesDropdown: React.FC<EmployeeRolesDropdownProps> = ({ 
    label, 
    availableRoles, 
    selectedRoles, 
    onSelectionChange, 
    isLoading 
}) => {
    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredWorkflows = availableRoles.filter((workflow) =>
        workflow.os_employee_role_name?.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelection = (selectedRole: EmployeeRole) => {
        const isSelected = selectedRoles.some((wf) => wf.os_employee_role_id === selectedRole.os_employee_role_id);
        if (isSelected) {
            onSelectionChange(selectedRoles.filter((wf) => wf.os_employee_role_id !== selectedRole.os_employee_role_id));
        } else {
            onSelectionChange([...selectedRoles, selectedRole]);
        }
    };

    return (
        <div className="relative flex-1" ref={dropdownRef}>
            <label className="block text-gray-700 font-semibold mb-2">{label}</label>

            {/* Selected Values */}
            <div
                className="relative min-w-[200px] border rounded-lg p-2 cursor-pointer bg-white flex items-center justify-between"
                onClick={() => !isLoading && setIsOpen(!isOpen)}
            >
                <span className="truncate flex">
                    {selectedRoles.length > 0 ? selectedRoles.map((wf) => wf.os_employee_role_name).join(", ") : "Select access roles"}
                </span>
                <span className="ml-2">&#9662;</span> {/* Dropdown Arrow */}
            </div>

            {/* Dropdown Options */}
            {isOpen && (
                <div className="absolute z-10 flex flex-col mt-2 bg-white border rounded-lg shadow-lg min-w-[200px] max-w-[300px]">
                    {isLoading ? (
                        <div className="p-4 text-center">
                            <span className="animate-spin inline-block w-6 h-6 border-4 border-gray-300 border-t-blue-500 rounded-full"></span>
                        </div>
                    ) : (
                        <>
                            <input
                                type="text"
                                className="w-full p-2 border-b focus:outline-none"
                                placeholder="Search access roles..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />

                            <div className="max-h-40 overflow-y-auto">
                                {filteredWorkflows.length > 0 ? (
                                    filteredWorkflows.map((workflow) => (
                                        <label key={workflow.os_employee_role_id} className="flex items-center p-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedRoles.some((wf) => wf.os_employee_role_id === workflow.os_employee_role_id)}
                                                onChange={() => handleSelection(workflow)}
                                                className="mr-2"
                                            />
                                            {workflow.os_employee_role_name}
                                        </label>
                                    ))
                                ) : (
                                    <div className="p-2 text-gray-500 text-center">No workflows found</div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default EmployeeRolesDropdown;
